import React from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumbs from '../layout/Breadcrumbs';

const POLICIES = {
  faq: { title: 'Frequently Asked Questions', content: `
    <h3>How do I place a wholesale order?</h3>
    <p>Simply browse our varieties, add items to your cart, and proceed to checkout. You can pay via bank transfer or card.</p>
    <h3>What are your minimum order requirements?</h3>
    <p>There is no minimum order. However, volume discounts apply at higher quantity thresholds.</p>
    <h3>How do Weekly Specials work?</h3>
    <p>Weekly Specials are available Monday through Friday. Check our homepage for current specials and click "Book Specials Now" to order.</p>
    <h3>What is Prebook ordering?</h3>
    <p>Prebook allows you to reserve flowers in advance. Select a delivery date at least 3 weeks from your purchase date.</p>
    <h3>Do you deliver?</h3>
    <p>Yes! We offer both pickup and delivery options. Delivery availability depends on your location.</p>
  ` },

  terms: { title: 'Terms & Conditions', content: `
    <p class="mb-4"><strong>All prices are in CAD.</strong></p>
    <p class="mb-6">If you do not agree with these terms, please do not use this website.</p>

    <h3>1. Company Information</h3>
    <p>This website is operated by:<br/>
    Petals West<br/>
    975 Sherwin Rd<br/>
    Winnipeg, Manitoba<br/>
    Canada<br/>
    Email: gin@petalswest.com</p>

    <h3>2. Wholesale Business Use</h3>
    <p>The Petals West website is intended for registered business customers only. By creating an account, you confirm that:</p>
    <ul>
      <li>You are purchasing products for business or commercial purposes</li>
      <li>The information you provide is accurate and complete</li>
      <li>You are authorized to place orders on behalf of your business</li>
    </ul>
    <p>Petals West reserves the right to approve, deny, or revoke account access at its discretion.</p>

    <h3>3. Account Registration</h3>
    <p>To place orders through our website, customers must create an account. Account holders agree to:</p>
    <ul>
      <li>Maintain accurate account information</li>
      <li>Keep login credentials confidential</li>
      <li>Accept responsibility for all activity under their account</li>
    </ul>
    <p>Petals West is not responsible for unauthorized access resulting from failure to secure account credentials.</p>

    <h3>4. Product Availability</h3>
    <p>Petals West supplies perishable floral products, and availability may vary based on seasonal supply, farm availability, and shipping conditions. We cannot guarantee that all products displayed on the website will be available at the time of order fulfillment.</p>
    <p>Petals West reserves the right to:</p>
    <ul>
      <li>Substitute products of similar quality when necessary</li>
      <li>Adjust quantities based on supply availability</li>
      <li>Cancel orders when products are unavailable</li>
    </ul>
    <p>Customers will be notified whenever possible.</p>

    <h3>5. Pricing</h3>
    <p>All pricing listed on the website:</p>
    <ul>
      <li>Is subject to change without notice</li>
      <li>May vary based on market conditions and supplier costs</li>
      <li>Is exclusive of applicable taxes and delivery fees unless otherwise stated</li>
    </ul>
    <p>Petals West reserves the right to correct pricing errors that may appear on the website.</p>

    <h3>6. Orders</h3>
    <p>Orders placed through the website are considered requests for product and are subject to confirmation by Petals West.</p>
    <p>Petals West reserves the right to:</p>
    <ul>
      <li>Refuse or cancel orders</li>
      <li>Substitute products of similar quality when necessary</li>
      <li>Limit quantities</li>
      <li>Adjust orders due to product availability</li>
    </ul>
    <p>Customers will be notified if adjustments are required.</p>

    <h3>7. Payment Terms</h3>
    <p>Payment terms are subject to the customer's approved account status. Accepted payment methods may include:</p>
    <ul>
      <li>Approved account terms</li>
      <li>Credit card payments</li>
      <li>Other payment methods authorized by Petals West</li>
    </ul>
    <p>Failure to comply with payment terms may result in:</p>
    <ul>
      <li>Account suspension</li>
      <li>Order cancellation</li>
      <li>Suspension of credit privileges</li>
    </ul>

    <h3>8. Perishable Product Policy</h3>
    <p>Floral products are highly perishable and require proper care upon receipt. Customers are responsible for:</p>
    <ul>
      <li>Inspecting products immediately upon delivery or pickup</li>
      <li>Following appropriate storage and hydration practices</li>
    </ul>
    <p>Claims related to product quality must be reported to Petals West within 24 hours of receipt. Failure to report issues within this timeframe may result in denial of claims.</p>

    <h3>9. Order Changes and Cancellations</h3>
    <p>Due to the nature of floral procurement and logistics, orders may not be modified or cancelled after processing. Customers requesting order changes should contact Petals West as soon as possible. We will make reasonable efforts to accommodate requests but cannot guarantee modifications.</p>

    <h3>10. Limitation of Liability</h3>
    <p>To the fullest extent permitted by law, Petals West shall not be liable for:</p>
    <ul>
      <li>Indirect or consequential damages</li>
      <li>Business losses or lost profits</li>
      <li>Product damage resulting from improper handling after delivery</li>
    </ul>
    <p>Our liability is limited to the value of the products purchased.</p>

    <h3>11. Website Use</h3>
    <p>Users agree not to:</p>
    <ul>
      <li>Use the website for unlawful purposes</li>
      <li>Attempt to gain unauthorized access to accounts or systems</li>
      <li>Interfere with the operation or security of the website</li>
    </ul>
    <p>Petals West reserves the right to suspend access for violations of these terms.</p>

    <h3>12. Intellectual Property</h3>
    <p>All website content, including logos, images, text, product descriptions, and website design is the property of Petals West and may not be copied, reproduced, or distributed without written permission.</p>

    <h3>13. Privacy</h3>
    <p>Use of this website is also governed by our <a href="/policies/privacy">Privacy Policy</a>, which explains how personal information is collected and used.</p>

    <h3>14. Changes to Terms</h3>
    <p>Petals West may update these Terms and Conditions at any time without prior notice. Continued use of the website after updates constitutes acceptance of the revised terms.</p>

    <h3>15. Governing Law</h3>
    <p>These Terms and Conditions shall be governed by the laws of the Province of Manitoba and the laws of Canada.</p>
  ` },

  privacy: { title: 'Privacy Policy', content: `
    <p class="mb-6">By using our website, you agree to the collection and use of information in accordance with this policy.</p>

    <h3>1. Information We Collect</h3>
    <p>We may collect personal information that you voluntarily provide when you:</p>
    <ul>
      <li>Create an online customer account</li>
      <li>Place an order through our website</li>
      <li>Subscribe to our newsletter or marketing communications</li>
      <li>Contact us through our website</li>
    </ul>
    <p>The types of personal information we may collect include:</p>
    <ul>
      <li>Name</li>
      <li>Business name</li>
      <li>Email address</li>
      <li>Phone number</li>
      <li>Billing and shipping address</li>
      <li>Account login credentials</li>
      <li>Order history and purchasing information</li>
    </ul>
    <p>We only collect information necessary to provide our services and improve your experience with Petals West.</p>

    <h3>2. Automatically Collected Information</h3>
    <p>When you visit our website, certain information may be collected automatically through cookies or analytics technologies, including:</p>
    <ul>
      <li>IP address</li>
      <li>Browser type and device information</li>
      <li>Pages visited on our website</li>
      <li>Time and date of visits</li>
      <li>Referring websites</li>
    </ul>
    <p>This information helps us understand how visitors use our website and allows us to improve functionality and user experience.</p>

    <h3>3. Cookies and Analytics</h3>
    <p>Our website may use cookies and similar tracking technologies to enhance user experience and gather usage data. Cookies may be used to:</p>
    <ul>
      <li>Remember login sessions</li>
      <li>Maintain account functionality</li>
      <li>Analyze website traffic and performance</li>
      <li>Improve website content and services</li>
    </ul>
    <p>We may use third-party analytics services such as Google Analytics to help us understand website usage. These services may collect anonymous usage data. You can modify your browser settings to decline cookies if you prefer, although some website features may not function properly without them.</p>

    <h3>4. How We Use Your Information</h3>
    <p>Petals West may use your information for the following purposes:</p>
    <ul>
      <li>To create and manage customer accounts</li>
      <li>To process and fulfill orders</li>
      <li>To communicate with customers regarding orders or account activity</li>
      <li>To send newsletters, promotional materials, or marketing communications (with your consent)</li>
      <li>To improve our products, services, and website functionality</li>
      <li>To maintain internal records and business operations</li>
    </ul>
    <p>You may unsubscribe from marketing emails at any time by clicking the unsubscribe link in our communications.</p>

    <h3>5. Sharing of Information</h3>
    <p>Petals West does not sell, rent, or trade personal information to third parties. We may share information with trusted third parties only when necessary to operate our business, including:</p>
    <ul>
      <li>Payment processing providers</li>
      <li>Website hosting services</li>
      <li>Analytics providers</li>
      <li>Delivery and logistics partners</li>
    </ul>
    <p>These providers are required to protect your information and use it only for the services they provide on our behalf. We may also disclose information if required by law or to comply with legal obligations.</p>

    <h3>6. Data Security</h3>
    <p>We take reasonable administrative, technical, and physical measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. However, no method of internet transmission or electronic storage is completely secure, and we cannot guarantee absolute security.</p>

    <h3>7. Data Retention</h3>
    <p>We retain personal information only for as long as necessary to:</p>
    <ul>
      <li>Provide our services</li>
      <li>Maintain business records</li>
      <li>Comply with legal and regulatory obligations</li>
    </ul>
    <p>When information is no longer required, it will be securely deleted or anonymized.</p>

    <h3>8. Your Privacy Rights</h3>
    <p>Under Canadian privacy laws, you have the right to:</p>
    <ul>
      <li>Request access to personal information we hold about you</li>
      <li>Request corrections to inaccurate information</li>
      <li>Withdraw consent for marketing communications</li>
      <li>Ask questions about how your information is handled</li>
    </ul>
    <p>To make a request, please contact us using the information below.</p>

    <h3>9. Children's Privacy</h3>
    <p>Our website and services are intended for business customers and are not directed toward individuals under the age of 13. We do not knowingly collect personal information from children.</p>

    <h3>10. Updates to This Privacy Policy</h3>
    <p>Petals West may update this Privacy Policy periodically to reflect changes in our practices, services, or legal requirements. Updated versions will be posted on this page with the revised effective date.</p>
  ` },

  refund: { title: 'Returns & Refunds', content: `
    <p>Due to the perishable nature of our products, all sales are final. If you receive damaged or incorrect items, please contact us within 24 hours of delivery at <a href="mailto:gin@petalswest.com">gin@petalswest.com</a> with photos. We will work with you to resolve any issues promptly.</p>
    <p class="mt-4">Verified claims may result in a replacement product or credit applied to your account, at the discretion of Petals West. Refunds are not typically issued for perishable goods.</p>
  ` },

  shipping: { title: 'Shipping & Delivery Policy', content: `
    <p class="mb-4">At Petals West, we are committed to providing high-quality floral products and reliable delivery services. This policy outlines product quality standards, claim procedures, and shipping/delivery terms for all wholesale customers.</p>

    <h3>1. Product Quality Standards</h3>
    <p>Petals West provides fresh, high-quality flowers and plants suitable for commercial use. To ensure product quality, we adhere to the following standards:</p>
    <ul>
      <li>All flowers are harvested from trusted farms and suppliers.</li>
      <li>Products are packaged securely for transportation.</li>
      <li>Products meet industry standards for freshness, size, and appearance.</li>
    </ul>
    <p><strong>Customer Responsibility:</strong></p>
    <ul>
      <li>Inspect products immediately upon receipt or pickup.</li>
      <li>Store and hydrate flowers according to recommended guidelines.</li>
      <li>Ensure proper handling to maintain product longevity.</li>
    </ul>

    <h3>2. Reporting Product Issues</h3>
    <p>Due to the perishable nature of flowers, claims must be reported promptly.</p>
    <p><strong>Reporting Guidelines:</strong></p>
    <ul>
      <li>Notify Petals West within 24 hours of delivery or pickup.</li>
      <li>Provide the following information: order number, product name and quantity, detailed description of the issue, and photographs.</li>
    </ul>
    <p>Claims submitted after 24 hours may not be accepted. Verified claims may result in a replacement product or credit applied to your account, at the discretion of Petals West. Refunds are not typically issued for perishable goods.</p>

    <h3>3. Shipping & Delivery Terms</h3>
    <p>Petals West offers scheduled wholesale deliveries and pickup options.</p>
    <p><strong>Delivery Schedule:</strong> Deliveries are made according to pre-set routes and schedules. Delivery times are estimated and may vary due to traffic, weather, or supplier delays.</p>
    <p><strong>Delivery Responsibility:</strong> Petals West ensures products leave our facility in good condition. Once delivered to your location, customers assume responsibility for product care.</p>
    <p><strong>Pickup Orders:</strong> Pickup orders must be collected within the scheduled pickup window. Products not collected on time may be subject to spoilage; Petals West is not responsible for uncollected products.</p>

    <h3>4. Order Changes & Cancellations</h3>
    <p>Orders for perishable floral products cannot be modified or cancelled once processed. Customers are encouraged to place orders early to ensure product availability. Any requests for changes will be accommodated only at the discretion of Petals West.</p>

    <h3>5. Risk of Loss</h3>
    <p>Risk of loss passes to the customer upon delivery to the agreed location or pickup from Petals West. It is the customer's responsibility to handle and transport products carefully to prevent damage or spoilage.</p>

    <h3>6. Disclaimer</h3>
    <p>Petals West makes every effort to maintain consistent quality, but due to the perishable nature of floral products, minor imperfections may occur. Petals West is not liable for losses resulting from improper handling, storage, or late delivery after the products have left our facility.</p>

    <h3>7. Contact for Claims or Shipping Inquiries</h3>
    <p>For questions or claims regarding product quality, shipping, or delivery, please contact:</p>
    <p>
      Email: <a href="mailto:gin@petalswest.com">gin@petalswest.com</a><br/>
      Phone: 204-786-7750<br/>
      975 Sherwin Rd<br/>
      Winnipeg, Manitoba<br/>
      Canada
    </p>
  ` },
};

export default function PoliciesPage() {
  const { slug } = useParams();
  const policy = POLICIES[slug] || POLICIES.faq;

  return (
    <div className="pw-container py-2 pb-12">
      <Breadcrumbs items={[{ label: 'Policies', href: '/policies/faq' }, { label: policy.title }]} />
      <div className="max-w-3xl">
        <h1 className="text-2xl font-semibold tracking-wider uppercase mb-6">{policy.title}</h1>
        <div className="prose prose-sm max-w-none text-[#6f6a63] leading-7 text-justify [&_h3]:text-[#121212] [&_h3]:font-semibold [&_h3]:text-base [&_h3]:mt-6 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_li]:my-0.5 [&_a]:text-[#2f6b4f] [&_a]:underline" dangerouslySetInnerHTML={{ __html: policy.content }} />
      </div>
    </div>
  );
}
